// Typed URL search params for the audit-trail route (spec 16 §4).
//
// The filters live in the URL for a reason specific to THIS screen: the
// entity-scoped view — "everything that happened to THIS quarantine / product /
// work order" — is a deep link, and a deep link is how the trail stops being a
// dump and starts being a tool. `/audit?entity_type=qc_hold&entity_id=…` is the
// whole "History" affordance an entity screen needs, so the shape has to survive
// a reload, a share and a paste into a dispute email.
//
// Validation is defensive (the house pattern): an unknown or malformed param is
// DROPPED, never thrown on. A stale shared link must still open the screen.

/** The module namespaces `action_prefix` may name. Anything else is dropped. */
export const AUDIT_AREAS = [
  "identity",
  "catalogue",
  "onboarding",
  "inventory",
  "production",
  "quality",
  "waste",
  "files",
  "agent",
] as const;

export type AuditArea = (typeof AUDIT_AREAS)[number];

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function area(value: unknown): AuditArea | undefined {
  return typeof value === "string" && (AUDIT_AREAS as readonly string[]).includes(value)
    ? (value as AuditArea)
    : undefined;
}

/** An ISO date (`YYYY-MM-DD`) from a date input, or nothing. */
function isoDate(value: unknown): string | undefined {
  const raw = str(value);
  if (raw === undefined) return undefined;
  return /^\d{4}-\d{2}-\d{2}$/u.test(raw) && !Number.isNaN(new Date(raw).getTime())
    ? raw
    : undefined;
}

export interface AuditSearch {
  readonly entity_type?: string | undefined;
  readonly entity_id?: string | undefined;
  readonly actor?: string | undefined;
  readonly area?: AuditArea | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

export function validateAuditSearch(search: Record<string, unknown>): AuditSearch {
  return {
    entity_type: str(search["entity_type"]),
    entity_id: str(search["entity_id"]),
    actor: str(search["actor"]),
    area: area(search["area"]),
    from: isoDate(search["from"]),
    to: isoDate(search["to"]),
  };
}

/**
 * URL params → the API's query shape.
 *
 * Two translations happen here and nowhere else:
 *   * `area` → `action_prefix` with its trailing dot, because `production` as a
 *     bare prefix would also match a future `productionx.` namespace;
 *   * a `from`/`to` DATE → the instant the server compares against, with `to`
 *     pushed to the end of that day. A user who types "to: 20 August" means
 *     "including the 20th"; sending midnight would silently drop a day's
 *     entries and the page would look complete.
 *
 *     THE DAY IS THE READER'S OWN DAY, not UTC. `${date}T00:00:00.000Z` looks
 *     harmless and is not: in Almaty (UTC+5/+6) "20 August" would return
 *     20 Aug 06:00 → 21 Aug 05:59 local — the whole night shift of the 20th
 *     missing, six hours of the 21st included — while the timestamp column
 *     beside it renders in the viewer's clock and says "20–21 August". A filter
 *     that disagrees with the column next to it is a defect anywhere; on a
 *     screen whose output is quoted in a dispute it is a serious one. Building
 *     the bounds from the local calendar day makes the two agree by
 *     construction, because the column is rendered in the same clock.
 *
 *     The remaining gap, stated: for a viewer whose OS clock is not the plant's,
 *     "20 August" is still their 20 August rather than the factory's. Closing
 *     that needs the tenant's `default_timezone` on a client-visible endpoint
 *     (it exists server-side on `ResolvedTenant`, and no web endpoint exposes
 *     it today) plus the same zone passed to the column's formatter.
 *   * `entity_id` is dropped without `entity_type` — the API refuses the
 *     half-pair (ids are not unique across types), and sending it would turn a
 *     stale shared link into a 422 instead of a slightly-wider list.
 */
export function auditSearchToQuery(search: AuditSearch): {
  entity_type?: string;
  entity_id?: string;
  actor?: string;
  action_prefix?: string;
  from?: string;
  to?: string;
} {
  const hasPair = search.entity_type !== undefined && search.entity_id !== undefined;
  return {
    ...(search.entity_type !== undefined ? { entity_type: search.entity_type } : {}),
    ...(hasPair ? { entity_id: search.entity_id } : {}),
    ...(search.actor !== undefined ? { actor: search.actor } : {}),
    ...(search.area !== undefined ? { action_prefix: `${search.area}.` } : {}),
    ...(search.from !== undefined ? { from: localDayStart(search.from) } : {}),
    ...(search.to !== undefined ? { to: localDayEnd(search.to) } : {}),
  };
}

/** `YYYY-MM-DD` → the ISO instant of that day's first millisecond, LOCALLY. */
export function localDayStart(date: string): string {
  return localInstant(date, 0, 0, 0, 0);
}

/** `YYYY-MM-DD` → the ISO instant of that day's last millisecond, LOCALLY. */
export function localDayEnd(date: string): string {
  return localInstant(date, 23, 59, 59, 999);
}

function localInstant(date: string, h: number, m: number, s: number, ms: number): string {
  const [y, mo, d] = date.split("-").map((part) => Number.parseInt(part, 10));
  // `new Date(y, monthIndex, …)` is the LOCAL-calendar constructor — the one
  // that makes the bound mean the same day the column renders.
  return new Date(y ?? 1970, (mo ?? 1) - 1, d ?? 1, h, m, s, ms).toISOString();
}
